import { type ReactNode } from 'react';

import { styled, css } from 'styled-components';

import ssncBluePrismLogo from '../../assets/ssnc-blue-prism-logo.svg';
import workHqLogo from '../../assets/workhq-logo.svg';

/**
 * Mirrors the WorkHQ tenant login page (the Keycloak `workhq-customer-app`
 * theme), so the hand-off from this hub to a tenant's own login page reads as
 * one continuous sign-in flow. Colours, layout breakpoints and the blurred
 * gradient layers are lifted from that theme's platform.css/background.css
 * rather than bsds-core tokens on purpose: this is a brand surface that has to
 * match it pixel-for-pixel, and it's light-only there too.
 */

const BRAND_GRADIENT = '#5886D1 0%, #CDDFF0 30%, #D4F1F8 65%, #7CAEE6 100%';
const GLOW = 'rgba(254, 255, 255, 0.6) 0%, rgba(0, 184, 240, 0) 100%';
const GLOW_LILAC = 'rgba(254, 255, 255, 0.6) 0%, rgba(223, 216, 229, 0) 100%';
const BOTTOM_GLOW = `radial-gradient(ellipse farthest-side at bottom center, ${GLOW})`;
const BOTTOM_GLOW_LILAC = `radial-gradient(ellipse farthest-side at bottom center, ${GLOW_LILAC})`;
const CENTER_GLOW = `radial-gradient(ellipse farthest-side at center, ${GLOW})`;

// Empty layers stacked into the soft "cloud" backdrop; nth-child order matches
// the source theme's #layer1..#layer10.
const GLOW_LAYER_COUNT = 10;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  background-image: linear-gradient(257deg, ${BRAND_GRADIENT});

  & > span {
    position: absolute;
  }

  & > span:nth-child(1) {
    width: 64.06vw;
    height: 58.33vh;
    left: 26.04vw;
    top: 23.61vh;
    background: #feffff;
    filter: blur(15.63vw);
    border-radius: 10.21vw;
    transform: rotate(2.07deg);
  }

  & > span:nth-child(2) {
    width: 62.5vw;
    height: 83.33vh;
    bottom: 7.41vh;
    background-image: ${BOTTOM_GLOW}, ${BOTTOM_GLOW};
    transform: rotate(-10deg);
    filter: blur(4.17vw);
  }

  & > span:nth-child(3) {
    width: 54.69vw;
    height: 60.56vh;
    right: 37.5vw;
    top: 18.52vh;
    background-image: ${CENTER_GLOW};
    filter: blur(6.25vw);
    border-radius: 10.42vw;
  }

  & > span:nth-child(4) {
    width: 44.79vw;
    height: 60.56vh;
    right: 11.46vw;
    top: 9.26vh;
    background-image: ${CENTER_GLOW};
    filter: blur(6.25vw);
    border-radius: 10.42vw;
    transform: rotate(30deg);
  }

  & > span:nth-child(5) {
    width: 31.25vw;
    height: 64.81vh;
    right: 20.83vw;
    top: 27.78vh;
    background-image: ${BOTTOM_GLOW}, ${BOTTOM_GLOW};
    transform: rotate(120deg);
    filter: blur(4.17vw);
  }

  & > span:nth-child(6) {
    width: 46.88vw;
    height: 111.11vh;
    right: 20.83vw;
    bottom: -27.78vh;
    background-image: ${BOTTOM_GLOW}, ${BOTTOM_GLOW};
    transform: rotate(-20deg);
    filter: blur(4.17vw);
  }

  & > span:nth-child(7) {
    width: 33.85vw;
    height: 32.41vh;
    right: 39.58vw;
    top: 32.41vh;
    background-image: radial-gradient(circle farthest-side at center, ${GLOW});
    filter: blur(4.69vw);
    border-radius: 10.42vw;
    transform: rotate(60deg);
  }

  & > span:nth-child(8) {
    width: 31.25vw;
    height: 111.11vh;
    right: 10.42vw;
    bottom: -33.33vh;
    background-image: ${BOTTOM_GLOW_LILAC}, ${BOTTOM_GLOW};
    transform: rotate(-60deg);
    filter: blur(6.25vw);
    border-radius: 10.42vw;
  }

  & > span:nth-child(9) {
    width: 23.44vw;
    height: 83.33vh;
    right: 26.04vw;
    bottom: -33.33vh;
    background-image: ${BOTTOM_GLOW_LILAC}, ${BOTTOM_GLOW};
    transform: rotate(-60deg);
    filter: blur(6.25vw);
    border-radius: 10.42vw;
  }

  /* Not positioned in the source theme either, so it paints beneath layers 1-9. */
  & > span:nth-child(10) {
    position: static;
    display: block;
    width: 100%;
    height: 100%;
    background-image: linear-gradient(224deg, ${BRAND_GRADIENT});
  }
`;

const Page = styled.div(
  ({ theme }) => css`
    position: relative;
    display: flex;
    align-items: center;
    min-height: 100vh;
    padding: 2rem;
    font-family: ${theme.font.family.alternate};
    color: #434a4f;

    @media (min-width: 640px) {
      padding: 2rem 4rem;
    }

    @media (min-width: 1024px) {
      padding-inline: 10rem;
    }

    @media (min-width: 1920px) {
      padding-inline: 14rem;
    }
  `,
);

const Content = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  gap: 4rem;
  width: 100%;

  @media (min-width: 1200px) {
    flex-direction: row;
    justify-content: flex-start;
    gap: 6rem;
  }

  @media (min-width: 1440px) {
    gap: 8rem;
  }

  @media (min-width: 1920px) {
    gap: 12rem;
  }
`;

const Card = styled.main`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  padding: 2rem;
  border-radius: 0.6667rem;
  background: #fff;
  box-shadow:
    0 2px 48px 0 rgba(7, 63, 140, 0.16),
    0 12px 16px 0 rgba(7, 63, 140, 0.1),
    0 4px 6px 0 rgba(7, 63, 140, 0.01);

  @media (min-width: 640px) {
    width: 80%;
  }

  @media (min-width: 1200px) {
    flex: 0 0 auto;
    width: 32rem;
    max-width: 70%;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 2rem;
  font-weight: 400;
  line-height: 1.5;
  color: #525d6d;
`;

const Body = styled.div`
  font-size: 1rem;
  line-height: 1.5;

  & > p {
    margin: 0;
  }
`;

const FooterLogo = styled.img`
  display: block;
  width: 10.75rem;
  height: 2.5rem;
`;

const MainLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;

  @media (min-width: 1025px) {
    justify-content: flex-start;
  }

  & > img {
    max-width: 70%;

    @media (min-width: 1025px) {
      max-width: 100%;
    }
  }
`;

interface BrandedShellProps {
  title: string;
  children: ReactNode;
}

export function BrandedShell({ title, children }: BrandedShellProps) {
  return (
    <>
      <Backdrop aria-hidden="true">
        {Array.from({ length: GLOW_LAYER_COUNT }, (_, index) => (
          <span key={index} />
        ))}
      </Backdrop>
      <Page>
        <Content>
          <Card>
            <Title>{title}</Title>
            <Body>{children}</Body>
            <footer>
              <FooterLogo src={ssncBluePrismLogo} alt="SS&C Blue Prism" />
            </footer>
          </Card>
          <MainLogo>
            <img src={workHqLogo} alt="SS&C Blue Prism WorkHQ" />
          </MainLogo>
        </Content>
      </Page>
    </>
  );
}
